import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { LocalRoutesNavTargets } from "@/LocalRoutes";
import { useAuth } from "@/hooks/use-auth";
import { FormikInput } from "./formik-input";
import { PasswordFieldWithRequirements } from "./password-field-with-requirements";
import { toast } from "sonner";

export const PasswordResetForm = ({
    email,
    code,
    className,
    ...props
}: ComponentPropsWithoutRef<"div"> & {
    email?: string | null;
    code?: string | null;
}) => {
    const { t } = useTranslation();
    const { forgotPasswordMutation, resetPasswordMutation } = useAuth();
    const navigate = useNavigate();

    return (
        <div className={cn("flex flex-col gap-6", className)} {...props}>
            <Card>
                <Formik
                    initialValues={{
                        email: email || "",
                        resetCode: code || "",
                        newPassword: "",
                        confirmPassword: "",
                        step: code ? 2 : 1,
                    }}
                    onSubmit={(values, { setFieldValue }) => {
                        if (values.step === 1) {
                            forgotPasswordMutation.mutate(
                                { email: values.email },
                                {
                                    onSuccess: (r) => {
                                        if (r.status === 200) {
                                            toast.success(
                                                t(
                                                    "components.password-reset-form.email-sent-success",
                                                ),
                                            );
                                            void setFieldValue("step", 2);
                                        }
                                    },
                                    onError: () => {
                                        toast.error(
                                            t(
                                                "components.password-reset-form.email-sent-error",
                                            ),
                                        );
                                    },
                                },
                            );
                        } else {
                            resetPasswordMutation.mutate(
                                {
                                    email: values.email,
                                    code: values.resetCode,
                                    newPassword: values.newPassword,
                                },
                                {
                                    onSuccess: (r) => {
                                        if (r.status === 200) {
                                            toast.success(
                                                t(
                                                    "components.password-reset-form.reset-success",
                                                ),
                                            );
                                            void navigate(
                                                LocalRoutesNavTargets.LOGIN,
                                            );
                                        }
                                    },
                                    onError: () => {
                                        toast.error(
                                            t(
                                                "components.password-reset-form.reset-error",
                                            ),
                                        );
                                    },
                                },
                            );
                        }
                    }}
                    validationSchema={Yup.object({
                        email: Yup.string()
                            .email(
                                t(
                                    "components.password-reset-form.label-email-valid",
                                ),
                            )
                            .required(
                                t(
                                    "components.password-reset-form.label-email-required",
                                ),
                            ),
                        resetCode: Yup.string().when("step", {
                            is: 2,
                            then: () =>
                                Yup.string().required(
                                    t(
                                        "components.password-reset-form.label-code-required",
                                    ),
                                ),
                        }),
                        newPassword: Yup.string().when("step", {
                            is: 2,
                            then: () =>
                                Yup.string()
                                    .required(
                                        t(
                                            "components.password-reset-form.label-password-required",
                                        ),
                                    )
                                    .min(
                                        6,
                                        t(
                                            "components.register-form.password-min-length",
                                        ),
                                    )
                                    .matches(
                                        /[a-z]/,
                                        t(
                                            "components.register-form.password-lowercase",
                                        ),
                                    )
                                    .matches(
                                        /[A-Z]/,
                                        t(
                                            "components.register-form.password-uppercase",
                                        ),
                                    )
                                    .matches(
                                        /[0-9]/,
                                        t(
                                            "components.register-form.password-digit",
                                        ),
                                    )
                                    .matches(
                                        /[^a-zA-Z0-9]/,
                                        t(
                                            "components.register-form.password-special",
                                        ),
                                    ),
                        }),
                        confirmPassword: Yup.string().when("step", {
                            is: 2,
                            then: () =>
                                Yup.string()
                                    .oneOf(
                                        [Yup.ref("newPassword")],
                                        t(
                                            "components.password-reset-form.label-passwords-match",
                                        ),
                                    )
                                    .required(
                                        t(
                                            "components.password-reset-form.label-confirm-required",
                                        ),
                                    ),
                        }),
                    })}
                >
                    {({ isValid, dirty, errors, values }) => (
                        <Form>
                            <CardHeader>
                                <CardTitle className="text-2xl">
                                    {t("components.password-reset-form.title")}
                                </CardTitle>
                                {values.step === 1 ? (
                                    <CardDescription>
                                        {t(
                                            "components.password-reset-form.description",
                                        )}
                                    </CardDescription>
                                ) : null}
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col gap-6">
                                    {values.step === 1 ? (
                                        <div className="grid gap-2">
                                            <Label htmlFor="email">
                                                {t(
                                                    "components.password-reset-form.label-email",
                                                )}
                                            </Label>
                                            <FormikInput
                                                id="email"
                                                name="email"
                                                placeholder="max@example.com"
                                                tabIndex={1}
                                                type="email"
                                            />
                                            {errors.email ? (
                                                <span className="text-xs text-red-600">
                                                    {errors.email}
                                                </span>
                                            ) : null}
                                            {forgotPasswordMutation.isError ? (
                                                <span className="text-xs text-red-600">
                                                    {t(
                                                        "components.password-reset-form.label-email-failed",
                                                    )}
                                                </span>
                                            ) : null}
                                        </div>
                                    ) : values.step === 2 && !code ? (
                                        <div className="grid gap-2">
                                            <div className="text-center text-sm text-muted-foreground">
                                                {t(
                                                    "components.password-reset-form.check-inbox-message",
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="grid gap-2">
                                                <Label htmlFor="resetCode">
                                                    {t(
                                                        "components.password-reset-form.label-code",
                                                    )}
                                                </Label>
                                                <FormikInput
                                                    id="resetCode"
                                                    name="resetCode"
                                                    tabIndex={1}
                                                    type="text"
                                                />
                                                {errors.resetCode ? (
                                                    <span className="text-xs text-red-600">
                                                        {errors.resetCode}
                                                    </span>
                                                ) : null}
                                            </div>
                                            <PasswordFieldWithRequirements
                                                id="newPassword"
                                                label={t(
                                                    "components.password-reset-form.label-new-password",
                                                )}
                                                name="newPassword"
                                                showRequirements={true}
                                                tabIndex={2}
                                            />
                                            <PasswordFieldWithRequirements
                                                id="confirmPassword"
                                                label={t(
                                                    "components.password-reset-form.label-confirm-password",
                                                )}
                                                name="confirmPassword"
                                                tabIndex={3}
                                            />
                                            {resetPasswordMutation.isError ? (
                                                <span className="text-xs text-red-600">
                                                    {t(
                                                        "components.password-reset-form.label-reset-failed",
                                                    )}
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                    {!(values.step === 2 && !code) ? (
                                        <Button
                                            className="w-full"
                                            disabled={
                                                !isValid ||
                                                !dirty ||
                                                forgotPasswordMutation.isPending ||
                                                resetPasswordMutation.isPending
                                            }
                                            tabIndex={values.step === 1 ? 2 : 4}
                                            type="submit"
                                        >
                                            {values.step === 1
                                                ? t(
                                                      "components.password-reset-form.action-send-code",
                                                  )
                                                : t(
                                                      "components.password-reset-form.action-reset-password",
                                                  )}
                                        </Button>
                                    ) : null}
                                </div>
                                <div className="mt-4 text-center text-sm">
                                    <Link
                                        className="underline underline-offset-4"
                                        to={LocalRoutesNavTargets.LOGIN}
                                    >
                                        {t(
                                            "components.password-reset-form.action-back-to-login",
                                        )}
                                    </Link>
                                </div>
                            </CardContent>
                        </Form>
                    )}
                </Formik>
            </Card>
        </div>
    );
};
